import React, { useState, useEffect } from "react";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  Divider,
} from "@mui/material";
import {
  Cloud as CloudIcon,
  WbSunny as SunIcon,
  AcUnit as SnowIcon,
  Thunderstorm as StormIcon,
  Water as RainIcon,
  Visibility as FogIcon,
  Refresh as RefreshIcon,
  LocationOn as LocationIcon,
  Thermostat as ThermostatIcon,
  Air as WindIcon,
  Opacity as HumidityIcon,
  Compress as PressureIcon,
  WbTwilight as SunsetIcon,
} from "@mui/icons-material";
import weatherService from "../services/weatherService";

const WeatherWidget = ({ location, onWeatherUpdate }) => {
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [summary, setSummary] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  useEffect(() => {
    if (location) {
      loadWeatherData();
    }

    // Subscribe to weather updates
    const unsubscribe = weatherService.subscribe((weatherData) => {
      setWeather(weatherData);
      setLastUpdated(new Date().toISOString());
      if (onWeatherUpdate) {
        onWeatherUpdate(weatherData);
      }
    });

    return unsubscribe;
  }, [location, onWeatherUpdate]);

  const loadWeatherData = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Load current weather
      const currentWeather = await weatherService.getCurrentLocationWeather();
      setWeather(currentWeather);

      // Load forecast
      const weatherForecast = await weatherService.getCurrentLocationForecast(
        3
      );
      setForecast(weatherForecast);

      // Load summary
      const weatherSummary = await weatherService.getCurrentLocationSummary();
      setSummary(weatherSummary);

      // Load recommendations
      const weatherRecommendations =
        await weatherService.getCurrentLocationRecommendations();
      setRecommendations(weatherRecommendations.recommendations || []);

      setLastUpdated(new Date().toISOString());
    } catch (error) {
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const refreshWeather = async () => {
    await loadWeatherData();
  };

  const getWeatherIcon = (description) => {
    const desc = description.toLowerCase();
    if (desc.includes("sun") || desc.includes("clear")) {
      return <SunIcon sx={{ fontSize: 40, color: "#FFA726" }} />;
    } else if (desc.includes("cloud")) {
      return <CloudIcon sx={{ fontSize: 40, color: "#90A4AE" }} />;
    } else if (desc.includes("rain")) {
      return <RainIcon sx={{ fontSize: 40, color: "#42A5F5" }} />;
    } else if (desc.includes("snow")) {
      return <SnowIcon sx={{ fontSize: 40, color: "#E3F2FD" }} />;
    } else if (desc.includes("storm")) {
      return <StormIcon sx={{ fontSize: 40, color: "#5C6BC0" }} />;
    } else if (desc.includes("fog") || desc.includes("mist")) {
      return <FogIcon sx={{ fontSize: 40, color: "#BDBDBD" }} />;
    } else {
      return <CloudIcon sx={{ fontSize: 40, color: "#90A4AE" }} />;
    }
  };

  const getTemperatureColor = (temp) => {
    if (temp < 0) return "#4A90E2";
    if (temp < 10) return "#7ED321";
    if (temp < 20) return "#F5A623";
    if (temp < 30) return "#D0021B";
    return "#9013FE";
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp).toLocaleDateString([], {
      weekday: "short",
      month: "short",
      day: "numeric",
    });
  };

  if (error) {
    return (
      <Card>
        <CardContent>
          <Alert severity="error">Failed to load weather data: {error}</Alert>
          <Box mt={2}>
            <IconButton onClick={refreshWeather} disabled={isLoading}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </CardContent>
      </Card>
    );
  }

  if (!weather && !isLoading) {
    return (
      <Card>
        <CardContent>
          <Typography variant="body2" color="text.secondary" align="center">
            No weather data available. Enable location access to see current
            weather.
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          <Box display="flex" alignItems="center" gap={1}>
            <LocationIcon color="primary" />
            <Typography variant="h6">Current Weather</Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            {lastUpdated && (
              <Typography variant="caption" color="text.secondary">
                Updated {formatTime(lastUpdated)}
              </Typography>
            )}
            <Tooltip title="Refresh Weather">
              <IconButton
                onClick={refreshWeather}
                disabled={isLoading}
                size="small"
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {isLoading && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            py={4}
          >
            <CircularProgress />
          </Box>
        )}

        {weather && (
          <>
            {/* Current Weather */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              mb={3}
            >
              <Box>
                <Typography
                  variant="h4"
                  fontWeight="bold"
                  color={getTemperatureColor(weather.temperature)}
                >
                  {weatherService.formatTemperature(weather.temperature)}
                </Typography>
                <Typography variant="h6" color="text.secondary">
                  {weather.location}
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{ textTransform: "capitalize" }}
                >
                  {weather.description}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Feels like{" "}
                  {weatherService.formatTemperature(weather.feels_like)}
                </Typography>
              </Box>
              <Box textAlign="center">
                {getWeatherIcon(weather.description)}
              </Box>
            </Box>

            {/* Weather Details */}
            <Grid container spacing={2} mb={3}>
              <Grid item xs={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <WindIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Wind
                    </Typography>
                    <Typography variant="body2">
                      {weather.wind_speed} m/s
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <HumidityIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Humidity
                    </Typography>
                    <Typography variant="body2">
                      {Math.round(weather.humidity)}%
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <PressureIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Pressure
                    </Typography>
                    <Typography variant="body2">
                      {Math.round(weather.pressure)} hPa
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <SunsetIcon color="action" />
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Sunset
                    </Typography>
                    <Typography variant="body2">
                      {formatTime(weather.sunset)}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Jarvis-style Summary */}
            {summary && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    🤖 Arya's Weather Summary
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {summary.summary}
                  </Typography>
                </Box>
              </>
            )}

            {/* Recommendations */}
            {recommendations.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    💡 Recommendations
                  </Typography>
                  <Box display="flex" flexWrap="wrap" gap={1}>
                    {recommendations.map((rec, index) => (
                      <Chip
                        key={index}
                        label={rec.title}
                        color={
                          rec.priority === "high"
                            ? "error"
                            : rec.priority === "medium"
                            ? "warning"
                            : "default"
                        }
                        size="small"
                        variant="outlined"
                      />
                    ))}
                  </Box>
                </Box>
              </>
            )}

            {/* Forecast */}
            {forecast && forecast.forecast && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" gutterBottom>
                    📅 3-Day Forecast
                  </Typography>
                  <Grid container spacing={1}>
                    {forecast.forecast.slice(0, 3).map((day, index) => (
                      <Grid item xs={4} key={index}>
                        <Box textAlign="center" p={1}>
                          <Typography variant="caption" color="text.secondary">
                            {formatDate(day.timestamp)}
                          </Typography>
                          <Box my={1}>{getWeatherIcon(day.description)}</Box>
                          <Typography variant="body2" fontWeight="medium">
                            {weatherService.formatTemperature(day.temperature)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {day.description}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              </>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default WeatherWidget;
